/**
 * Utilities para logging seguro e debugging
 */

// Função utilitária para debug de token JWT
export const debugJWT = (token: string) => {
  if (!token) {
    console.log("❌ Token não fornecido");
    return null;
  }

  try {
    // Separar as partes do JWT
    const parts = token.split(".");
    if (parts.length !== 3) {
      console.log("❌ Token JWT inválido - não tem 3 partes");
      return null;
    }

    // Decodificar header
    const header = JSON.parse(atob(parts[0]));
    console.log("🔍 JWT Header:", header);

    // Decodificar payload
    const payload = JSON.parse(atob(parts[1]));
    console.log("🔍 JWT Payload:", payload);

    // Verificar expiração
    if (payload.exp) {
      const expirationDate = new Date(payload.exp * 1000);
      const now = new Date();
      const isExpired = expirationDate < now;

      console.log("🔍 Token Expiration:", {
        exp: payload.exp,
        expirationDate: expirationDate.toISOString(),
        now: now.toISOString(),
        isExpired,
        minutesUntilExpiry: Math.round(
          (expirationDate.getTime() - now.getTime()) / 60000
        ),
      });

      return {
        header,
        payload,
        isExpired,
        expirationDate,
      };
    }

    return {
      header,
      payload,
      isExpired: false,
      expirationDate: null,
    };
  } catch (error) {
    console.error("❌ Erro ao decodificar JWT:", error);
    return null;
  }
};

// Função para testar token na API
export const testTokenInAPI = async (token: string) => {
  console.log("🧪 Testando token na API...");

  if (!token) {
    console.log("❌ Nenhum token fornecido para teste");
    return;
  }

  try {
    const response = await fetch(
      "https://portalchamadot4i-api-gec9hdfne0f5htcw.brazilsouth-01.azurewebsites.net/api/chamados/kanban",
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }
    );

    console.log("🧪 Status da resposta:", response.status);
    console.log(
      "🧪 Headers da resposta:",
      Object.fromEntries(response.headers.entries())
    );

    if (response.status === 401) {
      console.log("❌ Token rejeitado pela API (401)");
      const responseText = await response.text();
      console.log("📄 Resposta 401:", responseText);
    } else if (response.ok) {
      console.log("✅ Token aceito pela API");
      const data = await response.json();
      console.log("📊 Dados recebidos:", data);
    } else {
      console.log(`⚠️ Resposta inesperada: ${response.status}`);
      const responseText = await response.text();
      console.log("📄 Resposta:", responseText);
    }
  } catch (error) {
    console.error("❌ Erro na requisição de teste:", error);
  }
};

/**
 * Converte um objeto para uma representação segura para logging,
 * evitando referências circulares
 */
export const safeStringify = (obj: any, spaces = 2): string => {
  const seen = new WeakSet();

  try {
    return JSON.stringify(
      obj,
      (_key, value) => {
        // Evitar referências circulares
        if (typeof value === "object" && value !== null) {
          if (seen.has(value)) {
            return "[Circular Reference]";
          }
          seen.add(value);
        }

        // Substituir funções por string descritiva
        if (typeof value === "function") {
          return `[Function: ${value.name || "anonymous"}]`;
        }

        // Limitar arrays muito grandes
        if (Array.isArray(value) && value.length > 100) {
          return `[Array(${value.length}): ${value.slice(0, 3).join(", ")}...]`;
        }

        // Limitar objetos muito grandes
        if (
          typeof value === "object" &&
          value !== null &&
          Object.keys(value).length > 20
        ) {
          return "[Large Object]";
        }

        return value;
      },
      spaces
    );
  } catch (error) {
    return `[Unstringifiable: ${
      error instanceof Error ? error.message : "Unknown error"
    }]`;
  }
};

/**
 * Log seguro para desenvolvimento com formatação melhorada
 */
export const safeLog = {
  info: (message: string, data?: any) => {
    if (import.meta.env.DEV) {
      console.log(`ℹ️ ${message}`, data ? safeStringify(data) : "");
    }
  },

  warn: (message: string, data?: any) => {
    if (import.meta.env.DEV) {
      console.warn(`⚠️ ${message}`, data ? safeStringify(data) : "");
    }
  },

  error: (message: string, data?: any) => {
    if (import.meta.env.DEV) {
      console.error(`❌ ${message}`, data ? safeStringify(data) : "");
    }
  },

  debug: (message: string, data?: any) => {
    if (import.meta.env.DEV) {
      console.debug(`🐛 ${message}`, data ? safeStringify(data) : "");
    }
  },

  group: (title: string, callback: () => void) => {
    if (import.meta.env.DEV) {
      console.group(title);
      callback();
      console.groupEnd();
    }
  },
};

/**
 * Extrai apenas as propriedades serializáveis de um objeto
 */
export const extractSerializableProps = (obj: any): Record<string, any> => {
  if (obj === null || obj === undefined) return {};

  const result: Record<string, any> = {};

  try {
    for (const [key, value] of Object.entries(obj)) {
      if (
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean" ||
        value === null
      ) {
        result[key] = value;
      } else if (typeof value === "object" && value !== null) {
        // Tentar serializar objetos simples
        try {
          JSON.stringify(value);
          result[key] = value;
        } catch {
          result[key] = "[Complex Object]";
        }
      } else {
        result[key] = `[${typeof value}]`;
      }
    }
  } catch (error) {
    console.warn("Error extracting serializable props:", error);
  }

  return result;
};
